import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  PostFeeGroupsDTO,
  PostRateMarkupGroupsDTO,
  PutFeeDTO,
  PutFeeGroupsDTO,
  useGetCoinsQuery,
} from '../../../../api';
import { Option, TransactionDirection } from '../../../../enums';

type CreateExchangeFeeGroupModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  fields: PostRateMarkupGroupsDTO | null;
  fieldsHandler: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  isLoading: boolean;
  isCopy: boolean;
};

export const CreateExchangeFeeGroupModal: FC<CreateExchangeFeeGroupModalProps> = (props) => {
  const { open, onClose, onCreate, fields, fieldsHandler, isLoading, isCopy } = props;
  const { t } = useTranslation();

  // api
  const { data: coinList, isLoading: isCoinsLoading } = useGetCoinsQuery();

  // validation for required fields
  const requiredFields = ['name'] as (keyof PostRateMarkupGroupsDTO)[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  const optionSelectOptions = Object.values(Option).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {it}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>
        {isCopy
          ? t('exchange-fee-groups.modals.create.copy-title')
          : t('exchange-fee-groups.modals.create.title')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="name"
            value={fields?.name ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('exchange-fee-groups.modals.create.fields.name')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isCoinsLoading || isLoading || checkSubmitDisabling()}
        >
          {t('exchange-fee-groups.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('exchange-fee-groups.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
